"use client";

import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="116217" className="hidden md:flex gap-4">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-5 w-20 bg-slate-100 animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex ml-auto">
      <NavigationMenuList className="gap-2">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group inline-flex h-9 w-max items-center justify-center rounded-md px-4 py-2 text-sm font-semibold font-sans text-slate-900 tracking-tight transition-all duration-500 hover:text-[#1A5A8E] hover:bg-slate-50/50 focus:bg-transparent focus:text-[#1A5A8E] outline-none disabled:pointer-events-none disabled:opacity-50 data-[active=true]:text-[#1A5A8E] data-[active=true]:bg-transparent">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  isOpen,
  setIsOpen
}: {
  isOpen: boolean;
  setIsOpen: (v: boolean) => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (!isOpen) return null;
  return <div className="absolute top-full left-0 w-full bg-white border-b border-slate-100 shadow-2xl md:hidden flex flex-col py-6 px-6 gap-4 animate-in slide-in-from-top-2 duration-300">
      {loading ? <div className="flex flex-col gap-4">
          {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-6 w-32 bg-slate-100 animate-pulse rounded-md" />)}
        </div> : <nav className="flex flex-col gap-4 items-center text-center">
          {menuItems.map((item, i) => <div key={item.id} data-index={i} className="w-full">
              <Link to={item.href || "#"} className="text-lg font-semibold font-sans text-slate-900 hover:text-[#1A5A8E] transition-colors duration-300 block py-2" onClick={() => setIsOpen(false)}>
                {item.label}
              </Link>
            </div>)}
          <div className="pt-4 border-t border-slate-100 w-full">
            <Button className="w-full bg-gradient-to-r from-[#1A5A8E] to-[#4092C7] text-white hover:opacity-90 transition-all duration-300 font-sans font-bold shadow-md" onClick={() => setIsOpen(false)}>
              Request Demo
            </Button>
          </div>
        </nav>}
    </div>;
}
export default function GlobalNavigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        if (href === '/#') {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
          return;
        }
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (hashPart === '') {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
            return;
          }
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <header id="navigation-header" className={cn("text-slate-900 sticky top-0 z-50 w-full transition-all duration-500", isScrolled || isMobileMenuOpen ? "bg-white/80 backdrop-blur-md border-b border-slate-200/50 shadow-md" : "bg-white border-b border-transparent")}>
      <div className="container mx-auto px-4 md:px-6 h-20 flex items-center justify-between">
        {}
        <Link to="/HomePage" className="flex items-center z-50 relative">
          <img src="https://fond-python-1.10web.cloud/wp-content/uploads/2026/03/cannabis_cloud_Logo-1.png" alt="Cannabis Cloud" className="h-7" />
        </Link>

        {}
        <MenuProvider menu_id="39">
          <DesktopMenuConsumer />
        </MenuProvider>

        {}
        <div className="hidden md:flex items-center">
          <Button className="bg-gradient-to-r from-[#1A5A8E] to-[#4092C7] text-white hover:opacity-90 transition-all duration-300 font-sans font-bold h-10 px-6 shadow-lg animate-pulse">
            Request Demo
          </Button>
        </div>

        {}
        <Button variant="ghost" size="icon" className="ml-auto md:hidden text-slate-900 hover:bg-slate-100 hover:text-[#1A5A8E] transition-colors z-50 relative" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
          {isMobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
        </Button>

        {}
        <MenuProvider menu_id="39">
          <MobileMenuConsumer isOpen={isMobileMenuOpen} setIsOpen={setIsMobileMenuOpen} />
        </MenuProvider>
      </div>
    </header>;
}